// Implements: F-MCK-002, F-MCK-011, F-MCK-016, F-MCK-017, F-UIX-009, F-UIX-010, F-UIX-013
/**
 * The gallery (UX-07): tabs (Todos, Favoritos, Papelera), search, scope and tag filters, grid or list,
 * and the cards with their menu.
 *
 * The server filters: `q`, `tag`, `favorite`, `scope_kind` and `scope_id` go with every page request,
 * and the filters are part of the query key, so "Cargar más" and the filters never disagree. The
 * search waits for a pause in the typing (`searchDelayMs`, injected so no test sleeps). The tab lives
 * in the address (`?vista=favoritos|papelera`), the grid/list choice in the browser's storage (wrapped
 * in try/catch: a browser that refuses it simply forgets).
 *
 * Mockup v3 P8: the order (`sort`) is part of the filters, so a change of order starts its own list
 * from the first page; the choice is remembered like the grid/list one (`prototyper.panel.gallery-sort`,
 * try/catch). The tabs carry the counts of the chosen scope (`useGalleryCounts`, one request per
 * scope); while they are unknown or unreadable the tabs carry no number and nothing else changes. The pages are
 * flattened in order, so "load more" appends and never reorders what the person has under the cursor.
 * A failure never takes loaded data away: the error is shown inline and its retry asks for what failed.
 */

import { useCallback, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { useSession } from "../../../shared/api/session";
import { useTranslation } from "../../../shared/i18n";
import { Button, buttonClassName } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { tabPanelProps } from "../../../shared/ui/molecules/Tabs";
import { Dialog } from "../../../shared/ui/organisms/Dialog";
import { readPanelChoice, writePanelChoice } from "../../../shared/ui/panelMemory";
import type { GallerySort, MockupSummary } from "../../../types/api";
import { PLATFORM_LOCALE } from "../../../types/locale";
import { NO_FILTERS, hasFilters } from "../api/galleryFilters";
import type { GalleryFilters } from "../api/galleryFilters";
import { GALLERY_SORTS, GalleryToolbar } from "../components/GalleryToolbar";
import type { FilterOption, GalleryMode, GalleryTab } from "../components/GalleryToolbar";
import { GallerySkeleton } from "../components/GallerySkeleton";
import type { MockupActions } from "../components/mockupActions";
import { MockupCard } from "../components/MockupCard";
import { MockupTable } from "../components/MockupTable";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { buildScopeOptions, scopeLabelOf } from "../components/scopeOptions";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useGalleryCounts } from "../hooks/useGalleryCounts";
import { useMockupActions } from "../hooks/useMockupActions";
import { useMockupMenuActions } from "./useMockupMenuActions";
import { useMockups } from "../hooks/useMockups";
import styles from "./GalleryBrowserContainer.module.css";
import { RenameMockupContainer } from "./RenameMockupContainer";
import { ScopeChangeContainer } from "./ScopeChangeContainer";
import { TrashContainer } from "./TrashContainer";

const TABS_ID = "gallery-tabs";
const TAB_PARAM = "vista";
const VIEW_MEMORY_ID = "gallery-view";
const SORT_MEMORY_ID = "gallery-sort";
const MODES = ["grid", "list"] as const satisfies readonly GalleryMode[];
const ALL_SCOPES = "all";
const OWN_PERSONAL = "personal";
const NO_SEATS: readonly never[] = [];

const TAB_OF_PARAM: Readonly<Record<string, GalleryTab>> = { favoritos: "favorites", papelera: "trash" };
const PARAM_OF_TAB: Readonly<Record<GalleryTab, string | null>> = {
  all: null,
  favorites: "favoritos",
  trash: "papelera",
};

type Dialogs = { readonly kind: "rename" | "move"; readonly mockup: MockupSummary } | null;

export interface GalleryBrowserContainerProps {
  /** The search debounce in milliseconds (injected so no test sleeps). */
  readonly searchDelayMs?: number | undefined;
}

export function GalleryBrowserContainer({ searchDelayMs = 300 }: GalleryBrowserContainerProps): ReactElement {
  const { t } = useTranslation();
  const { state: session } = useSession();
  const navigate = useNavigate();
  const { push } = useToast();
  const { setFavorite, trash } = useMockupActions();
  const [params, setParams] = useSearchParams();
  const tab = TAB_OF_PARAM[params.get(TAB_PARAM) ?? ""] ?? "all";

  const [search, setSearch] = useState("");
  const [tag, setTag] = useState("");
  const [scope, setScope] = useState(ALL_SCOPES);
  const [mode, setMode] = useState<GalleryMode>(() => readPanelChoice(VIEW_MEMORY_ID, MODES) ?? "grid");
  const [sort, setSort] = useState<GallerySort>(() => readPanelChoice(SORT_MEMORY_ID, GALLERY_SORTS) ?? "recent");
  const [dialog, setDialog] = useState<Dialogs>(null);
  const [knownTags, setKnownTags] = useState<ReadonlySet<string>>(new Set());

  const seats = session.status === "authenticated" ? session.view.scopes : NO_SEATS;
  const viewerId = session.status === "authenticated" ? session.view.user.id : "";
  const scopeOptions = useMemo(
    () => [
      { value: ALL_SCOPES, label: t("gallery.scope.all"), kind: null, id: null },
      { value: OWN_PERSONAL, label: t("scope.personal"), kind: "personal", id: null },
      ...buildScopeOptions(seats).map((option) => ({ value: option.value, label: option.label, kind: option.kind, id: option.id })),
    ],
    [seats, t],
  );
  const chosenScope = scopeOptions.find((option) => option.value === scope);

  const debouncedSearch = useDebouncedValue(search, searchDelayMs);
  const filters: GalleryFilters = useMemo(
    () => ({
      q: debouncedSearch,
      tag: tag === "" ? null : tag,
      favorite: tab === "favorites",
      scope: chosenScope?.kind == null ? null : { kind: chosenScope.kind, id: chosenScope.id },
      sort,
    }),
    [debouncedSearch, tag, tab, chosenScope, sort],
  );
  const mockups = useMockups(filters, tab !== "trash");
  const counts = useGalleryCounts(filters.scope);
  const cards = mockups.data?.pages.flatMap((page) => page.items) ?? [];

  // Every tag seen so far stays in the menu, so choosing one does not make the others vanish.
  const unseen = cards.flatMap((card) => card.tags).filter((each) => !knownTags.has(each));
  if (unseen.length > 0) setKnownTags(new Set([...knownTags, ...unseen]));
  const menuTags = tag === "" || knownTags.has(tag) ? [...knownTags] : [...knownTags, tag];
  const tagOptions: readonly FilterOption[] = [
    { value: "", label: t("gallery.tag.all") },
    ...menuTags.sort((left, right) => left.localeCompare(right, PLATFORM_LOCALE)).map((each) => ({ value: each, label: each })),
  ];

  const changeTab = useCallback(
    (next: GalleryTab): void => {
      const value = PARAM_OF_TAB[next];
      setParams(value === null ? {} : { [TAB_PARAM]: value });
    },
    [setParams],
  );

  const changeMode = useCallback((next: GalleryMode): void => {
    setMode(next);
    writePanelChoice(VIEW_MEMORY_ID, next);
  }, []);

  const changeSort = useCallback((next: GallerySort): void => {
    setSort(next);
    writePanelChoice(SORT_MEMORY_ID, next);
  }, []);

  const clearFilters = (): void => {
    setSearch("");
    setTag("");
    setScope(ALL_SCOPES);
  };

  // Mockup v3 P4: Duplicar, Transferir and Exportar, with their dialogs (a slot P4 fills).
  const extra = useMockupMenuActions();
  const actions: MockupActions = {
    ...extra.actions,
    onToggleFavorite: (mockup) => {
      void setFavorite(mockup.id, !mockup.is_favorite).then((done) => {
        // `danger` is announced as an alert: a refusal that waits for a pause is a refusal missed.
        // The card has not changed, so nothing to undo.
        if (!done) push({ tone: "danger", message: t("gallery.favorite.failed") });
      });
    },
    onOpen: (mockup) => {
      void navigate(`/lienzo/${encodeURIComponent(mockup.id)}`);
    },
    onShare: (mockup) => {
      void navigate(`/lienzo/${encodeURIComponent(mockup.id)}?compartir=1`);
    },
    onRename: (mockup) => {
      setDialog({ kind: "rename", mockup });
    },
    onMove: (mockup) => {
      setDialog({ kind: "move", mockup });
    },
    onTrash: (mockup) => {
      void trash(mockup.id).then((done) => {
        push(
          done
            ? { tone: "success", message: t("gallery.card.trashed", { name: mockup.name }) }
            : { tone: "danger", message: t("gallery.card.trashFailed") },
        );
      });
    },
  };

  const scopeLabel = (mockup: MockupSummary): string =>
    scopeLabelOf(mockup, seats, viewerId, { personal: t("scope.personal"), personalOther: t("scope.personalOther") });

  const narrowed = hasFilters({ ...NO_FILTERS, q: filters.q, tag: filters.tag, scope: filters.scope });
  const loaded = mockups.data !== undefined && !mockups.isError;
  const emptyGallery = tab === "all" && loaded && cards.length === 0 && !narrowed;

  const body: ReactElement =
    tab === "trash" ? (
      <TrashContainer />
    ) : (
      <>
        {mockups.isPending ? mode === "list" ? <TableSkeleton /> : <GallerySkeleton /> : null}

        {mockups.isError ? (
          <ErrorState
            error={mockups.error}
            onRetry={() => {
              void (mockups.isFetchNextPageError ? mockups.fetchNextPage() : mockups.refetch());
            }}
          />
        ) : null}

        {emptyGallery ? (
          <EmptyState
            icon="grid"
            title={t("gallery.empty.title")}
            body={t("gallery.empty.body")}
            action={
              <>
                <Link className={buttonClassName("primary")} to="/lienzo">
                  <Icon name="canvas" size="sm" />
                  {t("gallery.empty.action")}
                </Link>
                <Button
                  variant="secondary"
                  onClick={() => {
                    changeTab("trash");
                  }}
                >
                  {t("gallery.empty.trash")}
                </Button>
              </>
            }
          />
        ) : null}

        {loaded && cards.length === 0 && !emptyGallery ? (
          narrowed ? (
            <EmptyState
              icon="search"
              title={t("gallery.noResults.title")}
              body={t("gallery.noResults.body")}
              action={
                <Button variant="secondary" onClick={clearFilters}>
                  {t("gallery.noResults.clear")}
                </Button>
              }
            />
          ) : (
            <EmptyState icon="star" title={t("gallery.favoritesEmpty.title")} body={t("gallery.favoritesEmpty.body")} />
          )
        ) : null}

        {cards.length > 0 && mode === "grid" ? (
          <ul className={styles.grid}>
            {cards.map((mockup) => (
              <MockupCard key={mockup.id} mockup={mockup} scopeLabel={scopeLabel(mockup)} actions={actions} />
            ))}
          </ul>
        ) : null}
        {cards.length > 0 && mode === "list" ? (
          <MockupTable mockups={cards} scopeLabelOf={scopeLabel} actions={actions} />
        ) : null}

        {mockups.hasNextPage ? (
          <div className={styles.more}>
            <Button
              variant="secondary"
              loading={mockups.isFetchingNextPage}
              onClick={() => {
                void mockups.fetchNextPage();
              }}
            >
              {t("gallery.loadMore")}
            </Button>
          </div>
        ) : null}
      </>
    );

  return (
    <div className={styles.gallery}>
      <GalleryToolbar
        idBase={TABS_ID}
        tabsOnly={emptyGallery}
        tab={tab}
        onTab={changeTab}
        search={search}
        onSearch={setSearch}
        scope={{
          options: scopeOptions,
          selected: scope,
          onSelect: setScope,
        }}
        tag={menuTags.length === 0 ? null : { options: tagOptions, selected: tag, onSelect: setTag }}
        mode={mode}
        onMode={changeMode}
        sort={sort}
        onSort={changeSort}
        counts={counts.data ?? null}
      />
      <div className={styles.panel} {...tabPanelProps(TABS_ID, tab)}>
        {body}
      </div>

      {dialog?.kind === "rename" ? (
        <RenameMockupContainer
          key={dialog.mockup.id}
          mockupId={dialog.mockup.id}
          currentName={dialog.mockup.name}
          onClose={() => {
            setDialog(null);
          }}
        />
      ) : null}
      {dialog?.kind === "move" ? (
        <Dialog
          open
          title={t("gallery.move.title")}
          onClose={() => {
            setDialog(null);
          }}
        >
          <ScopeChangeContainer key={dialog.mockup.id} mockupId={dialog.mockup.id} />
        </Dialog>
      ) : null}
      {extra.dialogs}
    </div>
  );
}
